import React, { useState, useCallback, useEffect, useRef } from "react";

import Checkbox from "src/components/Checkbox/Checkbox";

import { INestedShowElement } from "../index";
import styles from "./ShowElementsDepthBuilder.module.scss";

interface IProps {
  showElementsNested: INestedShowElement[]
  onChange(element: string, level?: string, section?: string): void
  /** When true, all collapsible elements start collapsed */
  defaultCollapsed?: boolean
}

const ShowElementsDepthBuilder = ({ showElementsNested, onChange, defaultCollapsed }: IProps) => {
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const hasInitialized = useRef(false);

  // Set initial collapsed state once showElementsNested is populated
  useEffect(() => {
    if (defaultCollapsed && showElementsNested.length > 0 && !hasInitialized.current) {
      hasInitialized.current = true;
      setCollapsed(new Set(
        showElementsNested
          .filter(n => !!(n.elementSections?.length) || !!(n.showLevels.length))
          .map(n => n.element)
      ));
    }
  }, [defaultCollapsed, showElementsNested]);

  const toggleCollapse = useCallback((element: string) => {
    setCollapsed(prev => {
      const next = new Set(prev);
      if (next.has(element)) {
        next.delete(element);
      } else {
        next.add(element);
      }
      return next;
    });
  }, []);

  return (
    <>
      {showElementsNested.map((n, i) => {
        const hasChildren = !!(n.elementSections?.length) || !!(n.showLevels.length);
        const isCollapsed = collapsed.has(n.element);

        const headerRowClasses = [
          styles.elementRow,
          !hasChildren ? styles.noChildren : "",
          isCollapsed ? styles.collapsed : "",
        ].filter(Boolean).join(" ");

        if (!hasChildren) {
          // Flat element — no border wrapper, no toggle
          return (
            <div key={n.element} className={styles.plainRow}>
              <Checkbox
                key={n.element}
                id={n.element + i}
                label={n.element}
                value={n.elementSelected}
                onChange={() => onChange(n.element)}
              />
            </div>
          );
        }

        return (
          <div key={n.element} className={styles.elementSection}>
            <div className={headerRowClasses}>
              <Checkbox
                key={n.element}
                id={n.element + i}
                label={n.element}
                value={n.elementSelected}
                onChange={() => onChange(n.element)}
              />

              <span className={styles.spacer} />

              <button
                type="button"
                className={styles.collapseToggle}
                onClick={() => toggleCollapse(n.element)}
                title={isCollapsed ? "Expand" : "Collapse"}
                aria-expanded={!isCollapsed}
              >
                <span className={`${styles.chevron}${isCollapsed ? ` ${styles.collapsed}` : ""}`}>
                  ▾
                </span>
              </button>
            </div>

            <div className={`${styles.children}${isCollapsed ? ` ${styles.hidden}` : ""}`}>

              {!!n.elementSections?.length && <>
                {n.elementSections.map((section, si) => (
                  <div key={[n.element, section.section].join("-")} style={{ marginLeft: 12 }}>
                    <Checkbox
                      key={[n.element, section.section].join("-") + i + si}
                      id={[n.element, section.section].join("-")}
                      disabled={!n.elementSelected}
                      label={section.section}
                      value={section.sectionSelected}
                      onChange={() => {
                        onChange(n.element, undefined, section.section);
                      }}
                    />
                  </div>
                ))}
              </>}

              {!!n.showLevels.length && <>
                {n.showLevels.map((sl, li) => {
                  return (
                    <div key={[n.element, sl.level].join("-") + li + i}>
                      <div style={{ marginLeft: 12 }}>
                        <Checkbox
                          key={[n.element, sl.level].join("-") + i + li}
                          id={[n.element, sl.level].join("-")}
                          disabled={!n.elementSelected}
                          label={sl.level}
                          value={sl.levelSelected}
                          onChange={() => {
                            onChange(n.element, sl.level);
                          }}
                        />
                      </div>

                      {!!sl.sections?.length && <>
                        {sl.sections.map((section, si) => {
                          return (
                            <div key={[n.element, sl.level, section.section].join("-")} style={{ marginLeft: 36 }}>
                              <Checkbox
                                key={[n.element, sl.level, section.section].join("-") + i + li + si}
                                id={[n.element, sl.level, section.section].join("-")}
                                disabled={!sl.levelSelected}
                                label={section.section}
                                value={section.sectionSelected}
                                onChange={() => {
                                  onChange(n.element, sl.level, section.section);
                                }}
                              />
                            </div>
                          )
                        })}
                      </>}
                    </div>
                  )
                })}
              </>}

            </div>
          </div>
        )
      })}
    </>
  )
}

export { ShowElementsDepthBuilder }
